import axios, { AxiosRequestConfig } from "axios";
import { appConst } from "../AppConst";
import { IBaseRespone } from "../model/respone/IBaseRespone";

/**
 * Default request timeout (ms). Server hang → axios reject với code "ECONNABORTED"
 * thay vì user chờ vô thời hạn. 30s đủ cho slow query (eligibility 200 lớp ~3s) + buffer.
 * Override per-call qua `config.timeout` nếu endpoint nặng hơn (vd export Excel).
 */
const DEFAULT_TIMEOUT_MS = 30000;

export const saveAccessToken = (token: string) => {
    localStorage.setItem("access_token", token);
};

export const getAccessToken = (): string | null => {
    return localStorage.getItem("access_token");
};

export const clearAccessToken = () => {
    localStorage.removeItem("access_token");
};

const buildHeaders = (extra?: Record<string, string>) => {
    const token = getAccessToken();
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(extra || {}),
    };
};

const buildConfig = (extraHeaders?: Record<string, string>): AxiosRequestConfig => ({
    headers: buildHeaders(extraHeaders),
    timeout: DEFAULT_TIMEOUT_MS,
});

interface IRequestConfig {
    headers?: Record<string, string>;
}

const handleResponse = async <T = any>(res: any): Promise<IBaseRespone<T>> => {
    return res.data as IBaseRespone<T>;
};

const handleError = (err: any): IBaseRespone => {
    if (err?.response?.status === 401) {
        clearAccessToken();
        if (window.location.pathname !== "/login") {
            window.location.href = "/login";
        }
        return { is_success: false, status_code: 401, message: "Phiên đăng nhập đã hết hạn." };
    }
    // 429 Too Many Requests — rate limit. Đè message server bằng câu rõ ràng cho người dùng cuối,
    // áp dụng cho mọi endpoint (classes, register, ...). Server gốc: "...đợi giây lát".
    if (err?.response?.status === 429) {
        return {
            is_success: false,
            status_code: 429,
            message: "Bạn đang gửi quá nhiều yêu cầu. Vui lòng quay lại sau ít phút.",
        };
    }
    if (err?.response?.data) {
        return err.response.data as IBaseRespone;
    }
    // Axios timeout: code === "ECONNABORTED" và message chứa "timeout".
    // Phân biệt với network fail thật để UX message rõ hơn.
    if (err?.code === "ECONNABORTED") {
        return { is_success: false, status_code: 504, message: "Yêu cầu xử lý quá lâu. Vui lòng thử lại." };
    }
    return { is_success: false, status_code: 500, message: err?.message || "Lỗi kết nối máy chủ." };
};

export const apiClient = {
    get: async <T = any>(path: string, config?: IRequestConfig): Promise<IBaseRespone<T>> => {
        try {
            const res = await axios.get(`${appConst.baseApiURL}/${path}`, buildConfig(config?.headers));
            return handleResponse<T>(res);
        } catch (err) {
            return handleError(err);
        }
    },
    post: async <T = any>(path: string, data?: any, config?: IRequestConfig): Promise<IBaseRespone<T>> => {
        try {
            const res = await axios.post(`${appConst.baseApiURL}/${path}`, data || {}, buildConfig(config?.headers));
            return handleResponse<T>(res);
        } catch (err) {
            return handleError(err);
        }
    },
    put: async <T = any>(path: string, data?: any, config?: IRequestConfig): Promise<IBaseRespone<T>> => {
        try {
            const res = await axios.put(`${appConst.baseApiURL}/${path}`, data || {}, buildConfig(config?.headers));
            return handleResponse<T>(res);
        } catch (err) {
            return handleError(err);
        }
    },
    delete: async <T = any>(path: string, config?: IRequestConfig): Promise<IBaseRespone<T>> => {
        try {
            const res = await axios.delete(`${appConst.baseApiURL}/${path}`, buildConfig(config?.headers));
            return handleResponse<T>(res);
        } catch (err) {
            return handleError(err);
        }
    },
};
